<template>
  <div id="speakers" class="speakers-section px-4">
    <div class="speakers-section__card">
      <!-- Section header -->
      <div class="speakers-section__header">
        <h2 class="speakers-section__title">
          {{ caption || $t('Views.Lobby.LobbySpeakers.featuredSpeakers') }}
        </h2>
        <button
          v-if="activeSpeakers.length"
          type="button"
          class="speakers-section__count"
          :aria-label="
            $tc(
              'Views.Lobby.LobbySpeakers.viewAllSpeakersAria',
              activeSpeakers.length,
              {
                n: activeSpeakers.length
              }
            )
          "
          @click="showAllSpeakers"
        >
          {{ $t('Views.Lobby.LobbySpeakers.viewAllSpeakers') }}
        </button>
      </div>

      <!-- Active speaker name — mobile only, sits above the rail -->
      <div
        v-if="activeSpeakers.length && activeSpeaker"
        class="speakers-active-name"
        aria-hidden="true"
      >
        {{ activeSpeakerDisplayName }}
      </div>

      <!-- One-row slider — scrollbar hidden, nav via buttons on desktop -->
      <div v-if="activeSpeakers.length" class="speakers-rail-wrap">
        <button
          type="button"
          class="speakers-rail__nav speakers-rail__nav--prev"
          :aria-label="$t('Views.Lobby.LobbySpeakers.previousSpeakers')"
          :disabled="!canScrollLeft"
          @click="scrollBy(-1)"
        >
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>

        <div
          ref="rail"
          class="speakers-rail"
          @scroll.passive="updateScrollState"
        >
          <SpeakerSection
            v-for="(speaker, i) in activeSpeakers"
            :key="speaker.id || speaker.UserID"
            :speaker="speaker"
            class="speakers-rail__item"
            :class="{ 'is-active': i === activeIndex }"
          />
        </div>

        <button
          type="button"
          class="speakers-rail__nav speakers-rail__nav--next"
          :aria-label="$t('Views.Lobby.LobbySpeakers.nextSpeakers')"
          :disabled="!canScrollRight"
          @click="scrollBy(1)"
        >
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>

      <div v-else class="speakers-section__empty">
        <p class="text-sm" style="color: #6b7280">
          {{ $t('Views.Lobby.LobbySpeakers.noSpeakersAvailable') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SpeakerSection from '@/views/Lobby/SpeakerSection';
import SpeakersListModal from '@/components/SpeakersListModal';
import { getPusherInstance } from '@/utils';

export default {
  name: 'LobbySpeakers',
  components: { SpeakerSection },
  props: {
    options: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      activeIndex: 0
    };
  },
  created() {
    if (!this.allSpeakers || !this.allSpeakers.length) {
      this.fetchSpeakers();
    }
    // Live speaker updates (admin add/edit/delete), mirroring the way
    // AgendaSection binds `agenda-change`.
    getPusherInstance({ isReady: true }).bind('speaker-change', () => {
      this.fetchSpeakers();
    });
  },
  mounted() {
    this.$nextTick(this.updateScrollState);
    window.addEventListener('resize', this.updateScrollState);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollState);
  },
  watch: {
    activeSpeakers() {
      this.$nextTick(this.updateScrollState);
    }
  },
  computed: {
    ...mapGetters('Speakers', { allSpeakers: 'getItems' }),
    ...mapGetters('CurrentEvent', ['getEvent']),
    caption() {
      return (
        this.options?.label ||
        this.getEvent?.captions?.speakersCaption ||
        this.$t('Views.Lobby.LobbySpeakers.featuredSpeakers')
      );
    },
    activeSpeakers() {
      if (!this.allSpeakers) return [];
      const speakers = Array.isArray(this.allSpeakers)
        ? this.allSpeakers
        : Object.values(this.allSpeakers);
      return speakers.filter((s) => s && s.active !== false);
    },
    activeSpeaker() {
      return this.activeSpeakers[this.activeIndex] || null;
    },
    activeSpeakerDisplayName() {
      const s = this.activeSpeaker;
      if (!s) return '';
      const first = s.name || s.Firstname || '';
      const last = s.lastName || s.Lastname || '';
      return `${first} ${last}`.trim().toUpperCase();
    }
  },
  methods: {
    ...mapActions('Speakers', ['fetchSpeakers']),
    updateScrollState() {
      const el = this.$refs.rail;
      if (!el) {
        this.canScrollLeft = false;
        this.canScrollRight = false;
        return;
      }
      const max = el.scrollWidth - el.clientWidth;
      this.canScrollLeft = el.scrollLeft > 4;
      this.canScrollRight = el.scrollLeft < max - 4;
      this.updateActiveIndex();
    },
    updateActiveIndex() {
      const rail = this.$refs.rail;
      if (!rail) return;
      const items = rail.querySelectorAll('.speakers-rail__item');
      if (!items.length) return;
      const railRect = rail.getBoundingClientRect();
      const center = railRect.left + railRect.width / 2;
      let best = 0;
      let bestDist = Infinity;
      items.forEach((el, i) => {
        const r = el.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const d = Math.abs(c - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      if (best !== this.activeIndex) this.activeIndex = best;
    },
    scrollBy(direction) {
      const el = this.$refs.rail;
      if (!el) return;
      // Advance by roughly one card's worth so arrows feel responsive
      // on any viewport — fallback to 80% of the visible width.
      const first = el.querySelector('.speakers-rail__item');
      const step = first
        ? first.getBoundingClientRect().width + 16
        : el.clientWidth * 0.8;
      el.scrollBy({ left: direction * step, behavior: 'smooth' });
    },
    showAllSpeakers() {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SpeakersListModal,
        {},
        {
          clickToClose: true,
          adaptive: true,
          minWidth: 300,
          maxWidth: 560,
          width: '92%',
          height: 'auto'
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    }
  }
};
</script>

<style scoped lang="scss">
.speakers-section {
  // Unified vertical rhythm between lobby sections (FAQ, speakers,
  // sponsors) — 32px top/bottom on desktop, tighter on mobile so
  // the page doesn't feel stretched out.
  padding-top: 32px;
  padding-bottom: 32px;

  @media (max-width: 640px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  // Inner card — matches the chrome used by .about-card and
  // .join-community__card so all three lobby sections share the
  // same white-rounded-bordered look.
  &__card {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;

    @media (max-width: 640px) {
      padding: 18px 16px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    // At least 24px between the "Speakers" title row and the carousel
    // body beneath it — applies at every breakpoint.
    margin-bottom: 24px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
  }

  &__count {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    background: #f3f4f6;
    padding: 4px 12px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover,
    &:focus-visible {
      background: #e5e7eb;
      color: #111827;
      outline: none;
    }
  }

  &__empty {
    text-align: center;
    padding: 32px 0;
  }
}

// Active speaker label above the coverflow carousel — mobile only.
.speakers-active-name {
  display: none;
}

@media (max-width: 640px) {
  .speakers-active-name {
    display: block;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #6b7280;
    margin-bottom: 8px;
    min-height: 13px; // reserve height to prevent jump when empty
  }
}

.speakers-rail-wrap {
  position: relative;
  // Reserve room on either side so the nav buttons never overlap the
  // first/last card, and the rail aligns visually with the arrows.
  padding: 0 44px;

  // Mobile: drop the inner padding — the rail itself handles the
  // side previews via its own padding-left/right so neighbor cards
  // bleed in around the focused one.
  @media (max-width: 640px) {
    padding: 0;
  }
}

.speakers-rail {
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  // Hover lift room — negative margin keeps alignment unchanged.
  padding: 4px 4px 8px;
  margin: -4px -4px 0;

  // Hide the scrollbar across browsers — navigation is via buttons
  // (and touch swipe on mobile), not a draggable thumb.
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge legacy
  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  } // WebKit

  &__item {
    flex: 0 0 240px;
    max-width: 240px;
    scroll-snap-align: start;

    @media (max-width: 640px) {
      // Narrower focal card + wider side padding on the rail, so
      // users see the center card clearly plus ~40% of each neighbor
      // on either side — matches the reference coverflow layout.
      flex: 0 0 54%;
      max-width: 54%;
      scroll-snap-align: center;
      transition: filter 0.3s ease, opacity 0.3s ease, transform 0.3s ease;
      filter: blur(1.5px);
      opacity: 0.7;
      transform: scale(0.9);

      &.is-active {
        filter: none;
        opacity: 1;
        transform: scale(1);
      }
    }
  }

  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #111827;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: background 0.15s, box-shadow 0.15s, opacity 0.15s;

    &:hover:not(:disabled) {
      background: #f9fafb;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
      box-shadow: none;
    }

    &--prev {
      left: 0;
    }
    &--next {
      right: 0;
    }

    // Mobile: floating over the blurred side cards. Needs a denser
    // shadow + higher contrast so it clearly reads as a tappable
    // control against the speaker photo behind it.
    @media (max-width: 640px) {
      width: 40px;
      height: 40px;
      font-size: 14px;
      color: #111827;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.06);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);

      &:hover:not(:disabled) {
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.22);
      }

      &--prev {
        left: 8px;
      }
      &--next {
        right: 8px;
      }
    }
  }
}

// Mobile-only overrides for the speaker card inside the coverflow so
// each tile renders as a rounded portrait photo (no card chrome, no
// text below). Name/title live above the rail in .speakers-active-name.
@media (max-width: 640px) {
  .speakers-rail {
    // Center-snap requires side padding so neighboring cards peek in
    // on both ends. 23% on each side + a 54% focal card leaves
    // roughly 40% of each neighbor visible, matching the reference.
    padding-left: 23%;
    padding-right: 23%;
    gap: 10px;
    // Let scaled / blurred neighbors overflow without being clipped.
    padding-top: 6px;
    padding-bottom: 6px;
    overflow-y: visible;
  }

  // Hide info block only — the rest of the mobile avatar-fill
  // styling lives in the unscoped <style> block below so it wins
  // over Avatar.vue's own scoped specificity.
  .speakers-rail__item ::v-deep .speaker-card__info {
    display: none !important;
  }
}
</style>

<!--
  Unscoped block: fighting Avatar.vue's scoped styles from another
  scoped block requires matching attribute-selector specificity which
  is fragile. These rules target the mobile coverflow items globally
  and override the circular-avatar layout so the photo fills the
  whole portrait tile edge-to-edge.
-->
<style lang="scss">
@media (max-width: 640px) {
  .speakers-rail__item .speaker-card {
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    border-radius: 18px !important;
    overflow: hidden !important;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12) !important;
    cursor: pointer;
  }

  .speakers-rail__item .speaker-card:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12) !important;
    transform: none !important;
  }

  .speakers-rail__item .speaker-card__avatar-wrap {
    width: 100% !important;
    height: auto !important;
    aspect-ratio: 3 / 4;
    border-radius: 18px !important;
    margin-bottom: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
    // Fallback for browsers without aspect-ratio support — pad to 4/3
    // (portrait) using the padding-top trick; relative-positioned so
    // the Avatar inside can use absolute fill.
    position: relative;
  }

  .speakers-rail__item .speaker-card__avatar {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    inset: 0;
  }

  // Kill Avatar's padding-bottom:100% square trick — holder should
  // fill the full 3:4 portrait container instead of being square.
  .speakers-rail__item .avatar {
    width: 100% !important;
    height: 100% !important;
    border-radius: 18px !important;
    overflow: hidden;
  }

  .speakers-rail__item .avatar__holder {
    width: 100% !important;
    height: 100% !important;
    padding-bottom: 0 !important;
    border-radius: 18px !important;
    position: absolute !important;
    inset: 0;
  }

  .speakers-rail__item .avatar__image {
    border-radius: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    top: 0;
    left: 0;
  }

  .speakers-rail__item .avatar__initials {
    border-radius: 0 !important;
    inset: 0;
  }
}
</style>
